import React, { useMemo } from "react";
import { Box, Typography, Divider, Button, Chip, Alert } from "@mui/joy";
import { DataGridSetting } from "../../../components";
import { useSelectedSupplier } from "../../../components/WorkEstimation/components/hooks";

import { CheckCircle2, AlertCircle } from "lucide-react";

import { generateColumnDefs } from "./column-def";

interface InventoryItem {
    ProductName: string;
    ProductCategory: string;
    UnitPrice: number;
    Quantity: number | string;
    ManufacturerProductId?: string;
    ManufacturerName?: string;
}

interface Supplier {
    SupplierName: string;
    SupplierInventory: InventoryItem[];
}

type PurchaseStrategy = "single" | "multiple";

interface SupplierComparisonProps {
    comparisonResults: Supplier[];
    purchaseStrategy?: PurchaseStrategy;
    onClose?: () => void;
    onConfirm?: () => void;
    itemSupplierSelections?: Record<string, string>; // { productName: supplierName }
    onItemSupplierSelect?: (productName: string, supplierName: string) => void;
}

const SupplierComparison: React.FC<SupplierComparisonProps> = ({
    comparisonResults,
    purchaseStrategy = "single",
    onClose,
    onConfirm,
    itemSupplierSelections = {},
    onItemSupplierSelect,
}) => {
    const { selectedSupplier } = useSelectedSupplier();
    // Transform suppliers data into rows suitable for the grid
    const { rowData, totals, bestPrices } = useMemo(() => {
        if (!comparisonResults || comparisonResults.length === 0) {
            return { rowData: [], totals: {}, bestPrices: {} };
        }

        const productMap: Record<
            string,
            {
                ProductCategory: string;
                ProductName: string;
                [supplierName: string]: number | string;
                Quantity: number | string;
            }
        > = {};

        const supplierTotals: Record<string, number> = {};
        const bestPricePerProduct: Record<string, { supplier: string; price: number }> = {};

        // Collect all unique products and map prices per supplier
        comparisonResults.forEach((supplier) => {
            if (!supplier || !supplier.SupplierInventory) {
                return;
            }

            // Initialize total for this supplier
            supplierTotals[supplier.SupplierName] = 0;

            supplier.SupplierInventory.forEach((product) => {
                if (!product || !product.ProductName) {
                    return;
                }

                const key = product.ProductName;
                if (!productMap[key]) {
                    productMap[key] = {
                        ProductCategory: product.ProductCategory || "",
                        ProductName: product.ProductName,
                        Quantity: product.Quantity || 0,
                    };
                }
                const unitPrice = product.UnitPrice || 0;
                const quantity = product.Quantity || 0;
                const totalPrice = Number(unitPrice) * Number(quantity);
                productMap[key][supplier.SupplierName] = unitPrice;

                // Calculate total for this supplier
                supplierTotals[supplier.SupplierName] += totalPrice;

                // Track best price per product
                if (unitPrice > 0) {
                    if (!bestPricePerProduct[key] || totalPrice < bestPricePerProduct[key].price) {
                        bestPricePerProduct[key] = {
                            supplier: supplier.SupplierName,
                            price: totalPrice,
                        };
                    }
                }
            });
        });

        return { rowData: Object.values(productMap), totals: supplierTotals, bestPrices: bestPricePerProduct };
    }, [comparisonResults]);

    // Find best total supplier
    const bestTotalSupplier = useMemo(() => {
        if (Object.keys(totals).length === 0) return null;
        return Object.entries(totals).reduce((best, [name, total]) => {
            if (!best || total < best.total) {
                return { name, total };
            }
            return best;
        }, null as { name: string; total: number } | null);
    }, [totals]);

    if (comparisonResults.length === 0) {
        return (
            <Box p={3}>
                <Alert color="neutral" variant="soft" startDecorator={<AlertCircle size={16} />}>
                    <Typography level="body-sm">
                        No comparison data available. Please select suppliers and try again.
                    </Typography>
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <Box px={2} py={1.5} borderBottom="1px solid" borderColor="divider">
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography level="body-md" sx={{ fontWeight: 600 }}>
                        Supplier Price Comparison
                    </Typography>
                    {bestTotalSupplier && (
                        <Chip variant="soft" color="success" size="sm" startDecorator={<CheckCircle2 size={12} />}>
                            Best: {bestTotalSupplier.name} ($
                            {bestTotalSupplier.total.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            })}
                            )
                        </Chip>
                    )}
                </Box>
            </Box>

            <Box sx={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
                <DataGridSetting
                    autoHeight={false}
                    suppressQuickControl={true}
                    pagination={false}
                    rowHeight={48}
                    headerHeight={40}
                    gridData={[
                        ...rowData,
                        { isFooter: true, totals, ProductName: "", bestTotalSupplier: bestTotalSupplier?.name },
                        (() => {
                            const selectionRow: any = { isSelectionRow: true, ProductName: "", totals };
                            comparisonResults.forEach((supplier) => {
                                selectionRow[supplier.SupplierName] = supplier.SupplierName;
                            });
                            return selectionRow;
                        })(),
                    ]}
                    columnDefs={generateColumnDefs(
                        comparisonResults,
                        purchaseStrategy,
                        bestPrices,
                        bestTotalSupplier?.name,
                        itemSupplierSelections,
                        onItemSupplierSelect
                    )}
                />
            </Box>

            <Box px={3} py={2.5} borderTop="1px solid" borderColor="divider" sx={{ bgcolor: "background.surface" }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
                        {purchaseStrategy === "single"
                            ? "Select a supplier from the table above"
                            : "Click on price cells to select suppliers for each product"}
                    </Typography>
                    <Box display="flex" gap={1}>
                        <Button variant="outlined" color="neutral" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button
                            variant="solid"
                            color="primary"
                            disabled={
                                purchaseStrategy === "single"
                                    ? !selectedSupplier?.name
                                    : Object.keys(itemSupplierSelections).length === 0
                            }
                            onClick={() => {
                                if (onConfirm) {
                                    onConfirm();
                                } else if (onClose) {
                                    onClose();
                                }
                            }}
                        >
                            Confirm Selection
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export { SupplierComparison };
